import { useState, useEffect } from 'react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { useOneSignal } from '../hooks/useOneSignal';
import { useTranslation } from '../hooks/useTranslation';

const DISMISS_KEY = 'flymind_push_prompt_dismissed';

export function PushNotificationPrompt() {
  const { isSubscribed, isSupported, permissionState, playerId, requestPermission, syncToBackend } =
    useOneSignal();
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(true); // default hidden until we check
  const [enabling, setEnabling] = useState(false);
  const [showBlockedHelp, setShowBlockedHelp] = useState(false);

  useEffect(() => {
    const wasDismissed = localStorage.getItem(DISMISS_KEY) === 'true';
    setDismissed(wasDismissed);
  }, []);

  // Don't show if already subscribed or not supported by the browser at all
  if (isSubscribed || !isSupported) {
    return null;
  }

  // If the user previously denied the browser prompt, we keep the card visible
  // (even after a previous "Non ora") with instructions, because the only way
  // out is through the browser settings — dismissing forever would trap them.
  const isBlocked = permissionState === 'denied';
  if (!isBlocked && dismissed) {
    return null;
  }

  const handleEnable = async () => {
    setEnabling(true);
    try {
      const finalState = await requestPermission();
      if (finalState === 'denied') {
        // Browser refused to show the prompt (typically because the user
        // already said no in the past). Surface the unblock instructions.
        setShowBlockedHelp(true);
        return;
      }
      // Wait briefly for playerId to propagate via the subscription change
      // listener, then sync to backend.
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (playerId) {
        await syncToBackend(playerId);
      }
    } catch {
      // Swallow: state is already reflected via permissionState.
    } finally {
      setEnabling(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, 'true');
    setDismissed(true);
  };

  return (
    <Card variant="bordered" padding="md" className="mb-6">
      <div className="flex items-start gap-4">
        {/* Bell icon */}
        <div className="shrink-0 w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center">
          <svg
            className="w-5 h-5 text-primary-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
            />
          </svg>
        </div>

        <div className="flex-1 min-w-0">
          {(isBlocked || showBlockedHelp) ? (
            <>
              <h3 className="text-sm font-semibold text-text-primary">
                {t('push.blockedTitle')}
              </h3>
              <p className="text-xs text-text-secondary mt-0.5">
                {t('push.blockedDescription')}
              </p>
              <ol className="text-xs text-text-secondary mt-2 ml-4 list-decimal space-y-0.5">
                <li>{t('push.blockedStep1')}</li>
                <li>{t('push.blockedStep2')}</li>
                <li>{t('push.blockedStep3')}</li>
              </ol>
              <div className="flex items-center gap-2 mt-3">
                <Button size="sm" variant="outline" onClick={() => window.location.reload()}>
                  {t('push.reload')}
                </Button>
                <Button size="sm" variant="ghost" onClick={handleDismiss}>
                  {t('push.hide')}
                </Button>
              </div>
            </>
          ) : (
            <>
              <h3 className="text-sm font-semibold text-text-primary">
                {t('push.title')}
              </h3>
              <p className="text-xs text-text-secondary mt-0.5">
                {t('push.description')}
              </p>
              <div className="flex items-center gap-2 mt-3">
                <Button size="sm" onClick={handleEnable} isLoading={enabling}>
                  {t('push.enable')}
                </Button>
                <Button size="sm" variant="ghost" onClick={handleDismiss}>
                  {t('push.notNow')}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}
